<script setup lang="ts">
import { z } from 'zod'
import type { BugReport } from '~/types/bugs'
import { html2canvas } from 'html2canvas-pro'
import JSZip from 'jszip'
import { useTypedForm } from '~/composables/useTypedForm'
import { FileStorage } from '~/utils/files/fileStorage'
import { getBrowserInfo } from '~/utils/helpers.ts'
import type { UploadableFile } from '~/utils/files/uploadableFile.ts'

type BugReportFormEmits = {
  close: []
}

const emits = defineEmits<BugReportFormEmits>()

const bugStore = useBugsStore()

const route = useRoute()

const fileStorage = new FileStorage([
  'image/png',
  'image/jpeg',
  'image/gif',
  'video/mp4',
  'video/x-msvideo', // AVI
  'video/quicktime' // MOV
])

const { baseKey } = useI18n('components.organisms.forms.bug-report-form')

const { handleSubmit, meta, handleReset, formProps, isLoading } = useTypedForm<BugReport>(
  {
    id: 'bug-report',
    schema: {
      reproductionSteps: z.string().min(1),
      expectedOutcome: z.string().min(1),
      actualOutcome: z.string().min(1)
    }
  }
)

const submitBugReport = handleSubmit(async (values) => {
  const appElement = document.getElementById('__nuxt')

  if (!appElement) {
    return
  }

  const routeName = route.name?.toString().replaceAll('/', '')

  const fileName = `${routeName}_${dayjsUTC().format('YYYY_MM_DD_HH_mm_ss')}`

  let blob: Blob | null
  try {
    const canvas = await html2canvas(appElement)
    blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve))
  } catch {
    blob = null
  }

  const zip = new JSZip()

  if (blob) {
    zip.file(`screenshot-${fileName}.png`, blob)
  }

  const browserInfo = await getBrowserInfo()
  zip.file(`browser-info-${fileName}.json`, JSON.stringify(browserInfo, undefined, 4))

  const zipFile = await zip.generateAsync({ type: 'blob' })

  const file = new File([zipFile], `error_${fileName}.zip`)

  const result = await bugStore.submitBugReport(
    file,
    fileStorage.file.value as UploadableFile | null,
    values,
    routeName
  )

  if (result) {
    handleReset()
    emits('close')
  }
})

defineExpose({ formProps, meta, isLoading })
</script>

<template>
  <div>
    <FormWrapper
      v-if="formProps"
      v-slot="{ k }"
      v-bind="formProps"
      @submit="submitBugReport"
    >
      <FileDropZone v-model="fileStorage" vertical class="mt-6! mb-4! text-center" />

      <FormInput
        :name="k.reproductionSteps"
        :label="`${baseKey}.labels.reproduction-steps`"
      />
      <FormInput
        :name="k.expectedOutcome"
        :label="`${baseKey}.labels.expected-outcome`"
      />
      <FormInput :name="k.actualOutcome" :label="`${baseKey}.labels.actual-outcome`" />
    </FormWrapper>
  </div>
</template>

<style scoped></style>
